import React, {useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";

import Modal from "../Interface/Modal/Modal";
import Input from "../Interface/Input/Input";
import Button from "../Interface/Button/Button";
import {encodeParameters} from "../../utils";
import {addAction, editAction} from "../../redux/proposal";
import useResize from "../../hooks/useResize";

function ActionModal({isOpen, setIsOpen, editIndex}) {
    const dispatch = useDispatch();
    const actions = useSelector(store => store.proposal.actions);

    const [step, setStep] = React.useState(1);
    const [target, setTarget] = React.useState("");
    const [signature, setSignature] = React.useState("");
    const [value, setValue] = React.useState("");
    const [parameters, setParameters] = React.useState([]);

    React.useEffect(() => {
        if (editIndex != null && actions[editIndex]) {
            setTarget(actions[editIndex].target);
            setSignature(actions[editIndex].signature);
            setValue(actions[editIndex].value);
            setParameters(actions[editIndex].parameters);
        }
    }, [editIndex, actions]);

    const signatueRegex = /(.+)\((.+)?\)/;
    let parameterTypes = [];
    let functionName = "";
    const splitSignature = signature.match(signatueRegex);
    if (splitSignature) {
        functionName = splitSignature[1];
        parameterTypes = splitSignature[2] ? splitSignature[2].split(',') : [];
    }

    let encoded = "";
    let encodingError = null;
    try {
        encoded = encodeParameters(parameterTypes, parameters);
    } catch (err) {
        encodingError = err;
    }

    let backHandler = null;
    if (step === 0) {
        backHandler = () => setStep(3 + parameterTypes.length);
    } else if (step !== 1) {
        backHandler = () => setStep(step - 1);
    }

    const setParameter = (index, value) => {
        const newParameters = [...parameters];
        newParameters[index] = value;
        setParameters(newParameters);
    };

    const handleSubmitSignature = () => {
        if (splitSignature) {
            if (parameterTypes.length !== parameters.length) {
                setParameters(new Array(parameterTypes.length));
            }
            setStep(3);
        }
    };

    const handleSubmit = () => {
        const action = {
            target, signature, value, parameters
        }
        if (editIndex != null) {
            dispatch(editAction({index: editIndex, action: action}));
        } else {
            dispatch(addAction(action));
        }
        setIsOpen(false);
    };

    const { windowWidth } = useResize();

    const mobileView = useMemo(() => windowWidth <= 600, [windowWidth]);

    let content = null;
    if (step === 0) {
        content = (
            <div  style={mobileView ?
                {marginTop: 40, paddingLeft: 20, paddingRight: 20}
                    :
                {marginTop: 40, paddingLeft: 100, paddingRight: 100}
            }
            >
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Target:</span> {target}<br />
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Signature:</span> {signature} <br />
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Value:</span> {value.length === 0 ? 0 : value} <br />
                <br />
                {parameterTypes.map((pType, index) =>
                    <React.Fragment key={index}>
                        <span style={{color: "var(--grey0)", fontWeight: 600}}>{pType}:</span> {parameters[index]} <br />
                    </React.Fragment>
                )}
                <br />
                {encodingError ? (
                    <span style={{color: "var(--red)", fontWeight: 600}}>Encoding Error</span>
                ) : (
                    <React.Fragment>
                        <span style={{color: "var(--grey0)", fontWeight: 600}}>Call Data:</span><br />
                        <p style={{marginTop: 0, maxWidth: "100%", overflow: "scroll"}}>{encoded}</p>
                    </React.Fragment>
                )}
                <div style={{width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40}}>
                    <Button className="highlightedButton" disabled={encodingError} onClick={encodingError ? null : handleSubmit}>
                        Submit
                    </Button>
                </div>
            </div>
        );
    } else if (step === 1) {
        content = (
            <div  style={mobileView ?
                {marginTop: 40, paddingLeft: 20, paddingRight: 20}
                    :
                {marginTop: 40, paddingLeft: 100, paddingRight: 100}
            }
            >
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Target Address</span>
                <Input
                    value={target}
                    setValue={setTarget}
                    style={{marginTop: 5}}
                    placeholder="Enter address"
                />
                <div style={{width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40}}>
                    <Button className="highlightedButton" onClick={() => setStep(2)}>
                        Continue
                    </Button>
                </div>
            </div>
        );
    } else if (step === 2) {
        content = (
            <div  style={mobileView ?
                {marginTop: 40, paddingLeft: 20, paddingRight: 20}
                    :
                {marginTop: 40, paddingLeft: 100, paddingRight: 100}
            }
            >
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Function Signature</span>
                <Input
                    value={signature}
                    setValue={setSignature}
                    style={{marginTop: 5}}
                    placeholder="Enter signature"
                />
                <div style={{width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40}}>
                    <Button className="highlightedButton" onClick={handleSubmitSignature}>
                        Continue
                    </Button>
                </div>
            </div>
        );
    } else if (step === 3) {
        content = (
            <div  style={mobileView ?
                {marginTop: 40, paddingLeft: 20, paddingRight: 20}
                    :
                {marginTop: 40, paddingLeft: 100, paddingRight: 100}
            }
            >
                <span style={{color: "var(--grey0)", fontWeight: 600}}>Value</span>
                <Input
                    value={value}
                    setValue={setValue}
                    style={{marginTop: 5}}
                    placeholder="Amount passed with call"
                />
                <div style={{width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40}}>
                    <Button className="highlightedButton" onClick={() => setStep(parameterTypes.length === 0 ? 0 : 4)}>
                        Continue
                    </Button>
                </div>
            </div>
        );
    } else {
        const parameterIndex = step - 4;
        content = (
            <div  style={mobileView ?
                {marginTop: 40, paddingLeft: 20, paddingRight: 20}
                    :
                {marginTop: 40, paddingLeft: 100, paddingRight: 100}
            }
            >
                <span style={{color: "var(--grey0)", fontWeight: 600}}>{functionName}: {parameterTypes[parameterIndex]}</span>
                <Input
                    value={parameters[parameterIndex] ?? ""}
                    setValue={(value) => setParameter(parameterIndex, value)}
                    style={{marginTop: 5}}
                    placeholder="Parameter value"
                />
                <div style={{width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 40}}>
                    <Button
                        className="highlightedButton"
                        onClick={() => setStep(parameterIndex + 1 === parameterTypes.length ? 0 : parameterIndex + 5)}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Modal
            title={editIndex != null ? "Edit Action" : "Add action"}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            multistep
            backHandler={backHandler}
            height={step === 0 ? 425 : 300}
        >
            {content}
        </Modal>
    )
}

export default ActionModal;
